import type { ReactNode } from "react";
import { ApiError } from "@/api/client";
import { apiMessage, violationMessage } from "@/i18n/apiMessages";
import { useT } from "@/i18n/LocaleProvider";

/**
 * The shared pieces of the interface.
 *
 * Every colour here is a theme token (`tailwind.config.js`): `canvas` is the
 * page, `surface` a panel on it, `sunken` a quiet fill inside one, `line` a
 * hairline, `ink` and `muted` the two text weights, and `accent` the theme's
 * one colour. Red (`overspend`) means overspend or a refusal and nothing else.
 *
 * The design is a household ledger's: quiet rows, figures in a strict column,
 * and a double rule only under a figure that really is a final total. Panels
 * hold forms; lists are rows on the page, not a card per item.
 */

export function PageTitle({
  children,
  action,
  lead,
}: {
  children: ReactNode;
  action?: ReactNode;
  /** One sentence under the title saying what the page is for. */
  lead?: ReactNode;
}) {
  return (
    <div className="mb-6 sm:mb-8">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
        <h1 className="text-2xl font-semibold tracking-tight text-ink">{children}</h1>
        {action}
      </div>
      {lead && <p className="mt-2 max-w-prose text-sm text-muted">{lead}</p>}
    </div>
  );
}

/** A titled part of a page. The heading is a real h2, so the outline reads. */
export function Section({
  title,
  action,
  children,
  className = "",
}: {
  title: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`mt-10 first:mt-0 ${className}`}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <h2 className="text-base font-semibold text-ink">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

/**
 * A titled part of a page that starts folded away: for the detail somebody
 * opens on purpose, not the answer the page exists to give.
 *
 * A native `details`, so the keyboard, the screen reader and find-in-page all
 * work without a line of state. The heading stays a real h2 inside the summary,
 * so the page's outline reads the same open or closed.
 */
export function Collapsible({
  title,
  meta,
  children,
  testId,
  className = "",
}: {
  title: ReactNode;
  /** A short muted note beside the title, such as a count. */
  meta?: ReactNode;
  children: ReactNode;
  testId?: string;
  className?: string;
}) {
  return (
    <details
      className={`group mt-4 rounded-panel border border-line bg-surface first:mt-0 ${className}`}
      data-testid={testId}
    >
      <summary className="flex min-h-14 cursor-pointer list-none items-center gap-3 rounded-panel px-4 py-3 transition-colors hover:bg-sunken focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent group-open:rounded-b-none group-open:border-b group-open:border-line sm:px-5 [&::-webkit-details-marker]:hidden">
        <span
          aria-hidden="true"
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-sunken text-muted transition-colors group-open:bg-accent-soft group-open:text-accent-text"
        >
          <svg
            viewBox="0 0 16 16"
            className="h-3.5 w-3.5 transition-transform duration-200 group-open:rotate-90 motion-reduce:transition-none"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M6 3.5 10.5 8 6 12.5" />
          </svg>
        </span>
        <h2 className="text-base font-semibold text-ink">{title}</h2>
        {meta !== undefined && (
          <span className="num ml-auto rounded-full border border-line bg-sunken px-2.5 py-0.5 text-xs text-muted">
            {meta}
          </span>
        )}
      </summary>
      <div className="p-4 sm:p-5">{children}</div>
    </details>
  );
}

/** A raised area for a form or a group that belongs together. */
export function Panel({
  children,
  className = "",
  as: Tag = "div",
}: {
  children: ReactNode;
  className?: string;
  as?: "div" | "section" | "aside";
}) {
  return (
    <Tag className={`rounded-panel border border-line bg-surface p-5 sm:p-6 ${className}`}>
      {children}
    </Tag>
  );
}

export function Loading() {
  const t = useT();
  return (
    <p className="flex items-center gap-2 py-8 text-sm text-muted" role="status">
      <span
        aria-hidden="true"
        className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-line border-t-accent"
      />
      {t("common.loading")}
    </p>
  );
}

/**
 * Renders a failure without pretending to understand it.
 *
 * Per-field violations from a 422 are shown next to their inputs by the forms
 * themselves; this is for everything else, and it keeps the status code because
 * "403" and "500" mean very different things to whoever is reading.
 */
export function ErrorNote({ error }: { error: unknown }) {
  const t = useT();
  if (!error) return null;
  const text =
    error instanceof ApiError
      ? error.violations.length > 0
        ? error.violations.map((v) => `${v.field}: ${violationMessage(t, v)}`).join("; ")
        : `${error.status} — ${apiMessage(t, error)}`
      : String(error);

  return (
    <p
      role="alert"
      className="mt-4 rounded-control border border-overspend/30 bg-overspend-soft px-3 py-2 text-sm text-overspend"
    >
      {text}
    </p>
  );
}

/** An empty list is an invitation to act: say what goes here, and how. */
export function EmptyState({
  title,
  children,
  action,
}: {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="mt-6 rounded-panel border border-dashed border-line px-6 py-10 text-center">
      <p className="text-base font-medium text-ink">{title}</p>
      {children && <div className="mx-auto mt-2 max-w-prose text-sm text-muted">{children}</div>}
      {action && <div className="mt-5 flex justify-center">{action}</div>}
    </div>
  );
}

export function Field({
  label,
  htmlFor,
  hint,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  error?: string | undefined;
  children: ReactNode;
}) {
  return (
    <div className="mt-5 first:mt-0">
      <label htmlFor={htmlFor} className="block text-sm font-medium text-ink">
        {label}
      </label>
      {children}
      {hint && !error && <p className="mt-1.5 text-xs text-muted">{hint}</p>}
      {error && (
        <p className="mt-1.5 text-xs font-medium text-overspend" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * One of a few, always one chosen: expense or income, three months or twelve.
 *
 * Radios underneath, so it is a real radio group to a screen reader and to the
 * keyboard (arrow keys move the choice), drawn as a segmented bar because the
 * options are few and all worth seeing at once.
 */
export function Segmented<T extends string>({
  legend,
  name,
  value,
  options,
  onChange,
  hideLegend = false,
  className = "",
}: {
  legend: string;
  name: string;
  value: T;
  options: ReadonlyArray<{ value: T; label: string }>;
  onChange: (value: T) => void;
  hideLegend?: boolean;
  className?: string;
}) {
  return (
    <fieldset className={className}>
      <legend className={hideLegend ? "sr-only" : "block text-sm font-medium text-ink"}>
        {legend}
      </legend>
      <div
        className={`${hideLegend ? "" : "mt-1.5"} flex rounded-control border border-line bg-sunken p-1`}
      >
        {options.map((option) => {
          const checked = option.value === value;
          return (
            <label
              key={option.value}
              className={`flex min-h-11 flex-1 cursor-pointer items-center justify-center rounded-[0.375rem] px-3 text-center text-sm transition-colors has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-accent ${
                checked
                  ? "bg-surface font-medium text-ink shadow-sm ring-1 ring-line"
                  : "text-muted hover:text-ink"
              }`}
            >
              <input
                type="radio"
                name={name}
                value={option.value}
                checked={checked}
                onChange={() => onChange(option.value)}
                className="sr-only"
              />
              {option.label}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

/** A small neutral tag for a state: "paused", "archived", "achieved". */
export function Tag({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full border border-line bg-sunken px-2 py-0.5 text-xs text-muted">
      {children}
    </span>
  );
}

export const inputClass =
  "mt-1.5 block w-full rounded-control border border-line bg-surface px-3 py-2 text-sm text-ink " +
  "placeholder:text-muted/70 transition-colors " +
  "hover:border-ink/30 focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/25 " +
  "disabled:cursor-not-allowed disabled:bg-sunken disabled:text-muted";

export const buttonClass =
  "inline-flex items-center justify-center gap-2 rounded-control bg-accent px-4 py-2 text-sm " +
  "font-medium text-accent-ink shadow-sm transition-colors hover:bg-accent/90 " +
  "disabled:cursor-not-allowed disabled:opacity-50";

export const secondaryButtonClass =
  "inline-flex items-center justify-center gap-2 rounded-control border border-line bg-surface " +
  "px-4 py-2 text-sm font-medium text-ink transition-colors hover:bg-sunken " +
  "disabled:cursor-not-allowed disabled:opacity-50";

/** A button that is only words: for the third action in a row, never the first. */
export const quietButtonClass =
  "inline-flex items-center justify-center gap-1.5 rounded-control px-2 py-1.5 text-sm " +
  "font-medium text-accent-text transition-colors hover:bg-accent-soft " +
  "disabled:cursor-not-allowed disabled:opacity-50";

/** A link that reads as one, in the theme's accent. */
export const linkClass =
  "font-medium text-accent-text underline decoration-accent/30 underline-offset-2 " +
  "transition-colors hover:decoration-accent";

/** Rows on the page, divided by hairlines — the list style for every list. */
export const rowsClass = "divide-y divide-line border-y border-line";
